import cx from 'classnames';
import { useAtom } from 'jotai';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslations from 'next-translate/useTranslation';
import React, {
  FunctionComponent,
  Fragment,
  useCallback,
  useMemo,
  useState,
} from 'react';

import useLinkProps from '../../../../../hooks/use-link-props';
import CategoryHeader from '../../../categories/header';
import MenuLink from '../../../links/menu';
import TextLink from '../../../links/text';
import PartNameWithStock from '../common/part-name-with-stock';

import { CategoryListProps } from './default-list';
import { ListSeparator } from './default-list';
import generateLink from './generate-link';
import classes from './navigation.module.css';
import FeaturedLink from '../../../links/featured';
import { regexExpress } from '../../../../../utils/search-results';
import useLoggedUser from '../../../../../hooks/use-logged-user';
import AlertForm from '../../../../modules/v2/create-part-alert/form';
import BPModal from '../../../modal';
import PageTitleSEO from '../../typography/page-title';

const FilteredList: FunctionComponent<
  CategoryListProps & {
    search: string;
    slug?: string;
    showNoResultsButton?: boolean;
    replaceNavigationFn?: Function;
    vehicle?: any;
  }
> = ({
  groups,
  splitInColumns,
  slug = '',
  onItemClick,
  search,
  replaceNavigationFn,
  showNoResultsButton = true,
  vehicle,
}) => {
    const { locale, query } = useRouter();
    const { t } = useTranslations();
    const getLink = useLinkProps();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedInfo, setSelectedInfo] = useState({});
    const user = useLoggedUser();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const regex = useMemo(() => {
      return regexExpress(
        search
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
      );
    }, [search]);

    const partname = useCallback(
      (name: string) => {
        const nameNormalized = name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

        const normalizedSearchText = search
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase();

        const matchIndex = nameNormalized.indexOf(normalizedSearchText);

        if (matchIndex === -1) return <span>{name}</span>;

        const start = name.slice(0, matchIndex);
        const match = name.slice(
          matchIndex,
          matchIndex + normalizedSearchText.length
        );
        const end = name.slice(matchIndex + normalizedSearchText.length);

        return (
          <Fragment>
            {start}
            <span className="font-bold text-brand-100">{match}</span>
            {end}
          </Fragment>
        );
      },
      [regex]
    );

    if (groups.length === 0) {
      return (
        <div className={cx('text-center', 'mt-12')}>
          {showNoResultsButton ? (
            <>
              <p className={cx('mb-6')}>{t('common:didntFindWhatLookingFor')}</p>
              <Link {...getLink('categories')} passHref shallow legacyBehavior>
                <TextLink variant="BUTTON-PRIMARY" onClick={onItemClick}>
                  {t('common:viewFullCatalog')}
                </TextLink>
              </Link>{' '}
            </>
          ) : (
            t('common:noresults')
          )}
        </div>
      );
    }

    return (
      <><ul>
        {groups.map(({ id, name, parts, slug: categorySlug }) => (
          <li key={id} className={cx('my-2')}>
            <CategoryHeader index={0} id={id} name={name} parts={parts.length} />
            <ul className={cx('pt-2', { [classes.list]: splitInColumns })}>
              {Object.entries(parts).map(([group_id, g], partsIndex) => (
                <Fragment key={partsIndex}>
                  {parseInt(group_id, 10) > 0 && (
                    <ListSeparator
                      id={parseInt(group_id, 10)}
                      inColumns={!!splitInColumns}
                      t={t}
                      locale={locale}
                    />
                  )}
                  {g?.map(
                    ({
                      name,
                      id,
                      slug: partSlug,
                      total_products: totalPartProducts,
                      ...rest
                    }: TPart) => (
                      <li key={id}>
                        <div className="w-full inline-block">
                          {totalPartProducts <= 0 ? <>
                            <MenuLink onClick={() => {
                              setSelectedInfo({
                                subCategory: name,
                                partSlug: partSlug,
                              })
                              setIsModalOpen(true);
                              return
                            }}>
                              <PartNameWithStock
                                name={partname(name)}
                                slug={partSlug}
                                display={splitInColumns ? 'inline' : 'column'}
                                nameTextTag={'h4'}
                                totalStock={totalPartProducts}
                              />
                            </MenuLink>
                          </> : <>{!replaceNavigationFn ? (
                            <Link
                              {...generateLink({
                                getLink,
                                slug,
                                partSlug,
                                categorySlug,
                              })}
                              passHref
                              shallow
                              prefetch={false}
                              legacyBehavior
                            >
                              <MenuLink onClick={onItemClick}>
                                <PartNameWithStock
                                  name={partname(name)}
                                  slug={partSlug}
                                  display={splitInColumns ? 'inline' : 'column'}
                                  nameTextTag={'h4'}
                                  totalStock={totalPartProducts}
                                />
                              </MenuLink>
                            </Link>
                          ) : (
                            <MenuLink
                              onClick={() =>
                                replaceNavigationFn({
                                  name,
                                  id,
                                  slug: partSlug,
                                  total_products: totalPartProducts,
                                  ...rest,
                                })
                              }
                            >
                              <PartNameWithStock
                                name={partname(name)}
                                slug={partSlug}
                                display={splitInColumns ? 'inline' : 'column'}
                                nameTextTag={'h4'}
                                totalStock={totalPartProducts}
                              />
                            </MenuLink>
                          )}
                          </>}

                        </div>
                      </li>
                    )
                  )}
                </Fragment>
              ))}
            </ul>
            <ul className="pl-4 w-full pb-6 text-right">
              <div className="max-lg:text-center text-base lg:text-xl">
                <Link
                  {...getLink(
                    query.category
                      ? 'sub-categories-with-vehicle'
                      : 'sub-categories',
                    {
                      category: query.category ? query.category : categorySlug,
                      slug: query.category ? categorySlug : '',
                    }
                  )}
                  legacyBehavior
                  passHref
                  prefetch={false}
                  shallow
                >
                  <FeaturedLink
                    variant="blue"
                    className="text-[20px] max-lg:text-base"
                  >
                    {t(`product:see_more`)}
                  </FeaturedLink>
                </Link>
              </div>
            </ul>
          </li>
        ))}
      </ul>
        <BPModal
          variant="container"
          showClose={true}
          title={''}
          isOverflowVisible={false}
          isOpen={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
        >
          <>
            <div className={cx('container', 'm-auto', 'mt-6')}>
              <div className="flex flex-col gap-4">
                <PageTitleSEO
                  textAlign={'left'}
                  title={t('private-area:part-alerts-create.page-tile') as string}
                />
                <h3 className="text-[20px] text-left">
                  {t('private-area:part-alerts-create.page-subtitle')}
                </h3>
              </div>
              {!user && (
                <div className="container mx-auto mt-6 px-4 py-4 text-center bg-brand-10">
                  <p>{t('private-area:part-alerts-create.alert')}</p>
                </div>
              )}
              <AlertForm info={selectedInfo} vehicle={vehicle} />
            </div>
          </>
        </BPModal>
      </>
    );
  };
export default FilteredList;
